import { useCallback, useRef, useState } from 'react';
import { commitManifest, type CommitMutators } from './runner';
import { newRowSteps, updateRowSteps } from './sitePipelines';
import type { CommitSummary, RowCommitState } from './types';
import {
    useCreateGeographyMutation,
    useCreateSiteMutation,
    useSetActivityMutation,
    useUpdateSiteMutation,
    type PreviewSiteManifestResponse,
} from '../state/api/organisationEngineSlice';

export type UseCommit = {
    /** Per-row state keyed by 1-based row number. Updates as orchestration progresses. */
    rowStates: Record<number, RowCommitState>;
    /** True while a commit is in flight. */
    isCommitting: boolean;
    /** Final summary after the last completed run; `null` until the first run settles. */
    summary: CommitSummary | null;
    /** Kicks off the commit. */
    commit: () => Promise<void>;
    /**
     * Clears row state + summary so a fresh upload starts from a clean slate.
     * Without this, a re-upload after a failed commit would still display the
     * stale failure banner because the hook's internal state outlives the
     * route-state replacement.
     */
    reset: () => void;
};

type Args = {
    bcId: string;
    preview: PreviewSiteManifestResponse;
};

/**
 * Glues the engine's per-resource RTK Query mutations to the generic step
 * harness. This is also where the adapter from the pipeline's typed inputs
 * (`CommitSiteInput`, `CommitGeographyInput`, `CommitActivityInput`) to the
 * engine's wire-format request bodies happens — single-source-of-truth for
 * the `projectCode → business_central_id` and `isicCode → isic_code` rename.
 *
 * Maintains per-row state via a ref so successive step transitions inside a
 * single run don't get lost to React batching.
 */
const useCommit = ({ bcId, preview }: Args): UseCommit => {
    const [createSite] = useCreateSiteMutation();
    const [updateSite] = useUpdateSiteMutation();
    const [createGeography] = useCreateGeographyMutation();
    const [setActivity] = useSetActivityMutation();

    const [rowStates, setRowStates] = useState<Record<number, RowCommitState>>({});
    const [isCommitting, setIsCommitting] = useState(false);
    const [summary, setSummary] = useState<CommitSummary | null>(null);

    // The orchestration reports a fresh state object per step. Funnel it
    // through a ref so successive updates don't lose anything to React
    // batching.
    const rowStatesRef = useRef<Record<number, RowCommitState>>({});
    // Synchronous re-entrancy guard. `isCommitting` (state) flips on a
    // re-render cycle, so a double-click that fires two commit() calls in
    // quick succession would both see isCommitting=false and start two
    // concurrent runs that clobber each other's rowStatesRef.
    const isRunningRef = useRef(false);

    const commit = useCallback(async () => {
        if (isRunningRef.current) return;
        isRunningRef.current = true;
        setIsCommitting(true);
        setSummary(null);

        const mutators: CommitMutators = {
            createSite: body =>
                createSite({ bcId, body })
                    .unwrap()
                    .then(s => ({ id: s.id })),
            updateSite: (siteId, body) =>
                updateSite({ bcId, siteId, body })
                    .unwrap()
                    .then(() => undefined),
            // Wrap the flat lat/lng input into the engine's GeoJSON Feature
            // shape — the one place where pipelines and wire format differ.
            createGeography: (siteId, input) =>
                createGeography({
                    bcId,
                    siteId,
                    body: {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [input.longitude, input.latitude] },
                        properties: {},
                    },
                })
                    .unwrap()
                    .then(() => undefined),
            setActivity: (siteId, body) =>
                setActivity({ bcId, siteId, body })
                    .unwrap()
                    .then(() => undefined),
        };

        rowStatesRef.current = {};

        const finalSummary = await commitManifest({
            preview,
            mutators,
            newRowSteps,
            updateRowSteps,
            onRowUpdate: state => {
                rowStatesRef.current = { ...rowStatesRef.current, [state.rowNumber]: state };
                setRowStates(rowStatesRef.current);
            },
        });

        setSummary(finalSummary);
        setIsCommitting(false);
        isRunningRef.current = false;
    }, [bcId, preview, createSite, updateSite, createGeography, setActivity]);

    const reset = useCallback(() => {
        rowStatesRef.current = {};
        isRunningRef.current = false;
        setRowStates({});
        setSummary(null);
        setIsCommitting(false);
    }, []);

    return { rowStates, isCommitting, summary, commit, reset };
};

export default useCommit;
